var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { attachRegionsHover, attachNearestHover, attachIndexXHover, attachGridHover, attachBandYHover } from '../core/interaction';
import { registerDashboard } from '../../registryCore';
import { createWidgetGroup, num, setState } from '../../helpers';
import { getActiveDashboard } from '../../theme';
import { installChartRebuild } from '../core/refresh';
import { defaultLayout, dataBounds, computeTicks, addAxes, addGridLines } from '../../chartPrimitives';
import { histogramBins, boxStats, kdeSamples, normalQuantiles, hexbinCenters } from '../core/stats';
import { linearScale } from '../core/scales';
import { colorWithAlpha, mixColors } from '../../../utils/color';
/** Interpolate two CSS colors by t in [0,1]. */
function mixHexColors(low, high, t) {
    return mixColors(low, high, t);
}
function primaryAlpha(t) {
    var _a;
    var primary = getActiveDashboard().primary;
    var a = 0.2 + Math.min(1, Math.max(0, t)) * 0.8;
    return (_a = colorWithAlpha(primary, a)) !== null && _a !== void 0 ? _a : getActiveDashboard().heatmapHigh;
}
function plotChrome(app, group, width, height, bounds) {
    var layout = defaultLayout(width, height);
    var yTicks = computeTicks(bounds.min, bounds.max, 5);
    group.add(app.rect({ width: width, height: height, fill: getActiveDashboard().chartBg, listening: true }));
    group.add(app.rect({ x: layout.plotX, y: layout.plotY, width: layout.plotWidth, height: layout.plotHeight, fill: getActiveDashboard().chartPlot, listening: false }));
    addGridLines(app, group, layout, yTicks, bounds);
    addAxes(app, group, layout, bounds, yTicks);
    return layout;
}
registerDashboard('histogram', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var data = (_a = props.data) !== null && _a !== void 0 ? _a : [2, 3, 3, 4, 5, 5, 5, 6, 7, 8, 9, 12];
    var bins = histogramBins(data, num(props, 'binCount', 8));
    var max = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(bins.map(function (b) { return b.count; })), false), [1], false));
    var group = createWidgetGroup(app, 'histogram', props);
    var layout = plotChrome(app, group, width, height, { min: 0, max: max });
    var bw = layout.plotWidth / bins.length;
    bins.forEach(function (b, i) {
        var h = (b.count / max) * layout.plotHeight;
        group.add(app.rect({ x: layout.plotX + i * bw, y: layout.plotY + layout.plotHeight - h, width: bw - 2, height: h, fill: getActiveDashboard().barFill, listening: false }));
    });
    attachIndexXHover(app, group, props, layout, bins.length, function (i) { return "".concat(bins[i].x0.toFixed(1), "\u2013").concat(bins[i].x1.toFixed(1), ": ").concat(bins[i].count); }, function (i) {
        var h = (bins[i].count / max) * layout.plotHeight;
        return { x: layout.plotX + i * bw, y: layout.plotY + layout.plotHeight - h, width: bw - 2, height: h };
    });
    setState(group, { width: width, height: height, data: data });
    return group;
});
registerDashboard('boxPlot', function (props, app) {
    var _a, _b;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var sets = Array.isArray((_a = props.data) === null || _a === void 0 ? void 0 : _a[0])
        ? props.data
        : (_b = props.data) !== null && _b !== void 0 ? _b : [[2, 3, 4, 5, 6, 7, 8], [3, 5, 7, 9, 11]];
    var group = createWidgetGroup(app, 'boxPlot', props);
    var layout = plotChrome(app, group, width, height, dataBounds(sets.flat()));
    var slot = layout.plotWidth / sets.length;
    sets.forEach(function (vals, i) {
        var s = boxStats(vals);
        var cx = layout.plotX + slot * i + slot / 2;
        var scale = linearScale([s.min, s.max], [layout.plotY + layout.plotHeight, layout.plotY]);
        var yQ1 = scale(s.q1);
        var yQ3 = scale(s.q3);
        var yMed = scale(s.median);
        group.add(app.rect({ x: cx - 16, y: yQ3, width: 32, height: yQ1 - yQ3, fill: getActiveDashboard().chartArea, stroke: getActiveDashboard().chartLine, strokeWidth: 1, listening: false }), app.line({ x: cx - 16, y: yMed, x2: 32, y2: 0, stroke: getActiveDashboard().chartLine, strokeWidth: 2, listening: false }), app.line({ x: cx, y: scale(s.min), x2: 0, y2: scale(s.max) - scale(s.min), stroke: getActiveDashboard().textMuted, strokeWidth: 1, listening: false }));
    });
    attachIndexXHover(app, group, props, layout, sets.length, function (i) {
        var s = boxStats(sets[i]);
        return "med ".concat(s.median, " [").concat(s.q1, ", ").concat(s.q3, "]");
    });
    setState(group, { width: width, height: height, data: sets });
    return group;
});
registerDashboard('boxAndWhiskerChart', function (props, app) {
    var _a, _b;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var sets = Array.isArray((_a = props.data) === null || _a === void 0 ? void 0 : _a[0])
        ? props.data
        : (_b = props.data) !== null && _b !== void 0 ? _b : [[2, 3, 4, 5, 6, 7, 8], [3, 5, 7, 9, 11]];
    var group = createWidgetGroup(app, 'boxAndWhiskerChart', props);
    var layout = plotChrome(app, group, width, height, dataBounds(sets.flat()));
    var slot = layout.plotWidth / sets.length;
    sets.forEach(function (vals, i) {
        var s = boxStats(vals);
        var cx = layout.plotX + slot * i + slot / 2;
        var scale = linearScale([s.min, s.max], [layout.plotY + layout.plotHeight, layout.plotY]);
        var yQ1 = scale(s.q1);
        var yQ3 = scale(s.q3);
        var yMed = scale(s.median);
        group.add(app.rect({ x: cx - 16, y: yQ3, width: 32, height: yQ1 - yQ3, fill: getActiveDashboard().chartArea, stroke: getActiveDashboard().chartLine, strokeWidth: 1, listening: false }), app.line({ x: cx - 16, y: yMed, x2: 32, y2: 0, stroke: getActiveDashboard().chartLine, strokeWidth: 2, listening: false }), app.line({ x: cx, y: scale(s.min), x2: 0, y2: scale(s.max) - scale(s.min), stroke: getActiveDashboard().textMuted, strokeWidth: 1, listening: false }));
    });
    attachIndexXHover(app, group, props, layout, sets.length, function (i) {
        var s = boxStats(sets[i]);
        return "med ".concat(s.median, " [").concat(s.q1, ", ").concat(s.q3, "]");
    });
    setState(group, { width: width, height: height, data: sets });
    return group;
});
registerDashboard('violinPlot', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var data = (_a = props.data) !== null && _a !== void 0 ? _a : [2, 3, 3, 4, 5, 5, 6, 7, 8, 9];
    var group = createWidgetGroup(app, 'violinPlot', props);
    var layout = plotChrome(app, group, width, height, dataBounds(data));
    var samples = Array.from({ length: 20 }, function (_, i) { return layout.plotY + (layout.plotHeight / 19) * i; });
    var dens = kdeSamples(data, samples.map(function (y) { return layout.plotY + layout.plotHeight - (y - layout.plotY); }));
    var maxD = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(dens), false), [0.001], false));
    var cx = layout.plotX + layout.plotWidth / 2;
    var pts = [];
    dens.forEach(function (d, i) {
        var y = layout.plotY + (layout.plotHeight / dens.length) * i;
        pts.push(cx - (d / maxD) * 40, y, cx + (d / maxD) * 40, y);
    });
    group.add(app.polygon({ points: pts, fill: getActiveDashboard().chartArea, stroke: getActiveDashboard().chartLine, strokeWidth: 1, listening: false }));
    attachIndexXHover(app, group, props, layout, 20, function (i) {
        var y = layout.plotY + (layout.plotHeight / 19) * i;
        var v = layout.plotY + layout.plotHeight - (y - layout.plotY);
        return "density @ ".concat(v.toFixed(1));
    });
    setState(group, { width: width, height: height, data: data });
    return group;
});
registerDashboard('densityPlot', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var data = (_a = props.data) !== null && _a !== void 0 ? _a : [2, 3, 3, 4, 5, 5, 6, 7, 8, 9];
    var group = createWidgetGroup(app, 'densityPlot', props);
    var bounds = dataBounds(data);
    var layout = plotChrome(app, group, width, height, bounds);
    var xs = Array.from({ length: 40 }, function (_, i) { return bounds.min + ((bounds.max - bounds.min) * i) / 39; });
    var dens = kdeSamples(data, xs);
    var maxD = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(dens), false), [0.001], false));
    var pts = [];
    xs.forEach(function (_x, i) {
        var px = layout.plotX + (layout.plotWidth * i) / 39;
        var py = layout.plotY + layout.plotHeight - (dens[i] / maxD) * layout.plotHeight;
        pts.push(px, py);
    });
    group.add(app.polyline({ points: pts, fill: null, stroke: getActiveDashboard().chartLine, strokeWidth: 2, listening: false }));
    attachIndexXHover(app, group, props, layout, xs.length, function (i) { return "x=".concat(xs[i].toFixed(1), " \u03C1=").concat(dens[i].toFixed(3)); });
    setState(group, { width: width, height: height, data: data });
    return group;
});
registerDashboard('heatmap', function (props, app) {
    var _a, _b, _d;
    var width = num(props, 'width', 240);
    var height = num(props, 'height', 160);
    var matrix = (_a = props.matrix) !== null && _a !== void 0 ? _a : [
        [1, 3, 5, 2],
        [4, 1, 6, 3],
        [2, 5, 1, 4],
    ];
    var group = createWidgetGroup(app, 'heatmap', props);
    var max = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(matrix.flat()), false), [1], false));
    var rows = matrix.length;
    var cols = (_d = (_b = matrix[0]) === null || _b === void 0 ? void 0 : _b.length) !== null && _d !== void 0 ? _d : 1;
    var cw = width / cols;
    var ch = height / rows;
    matrix.forEach(function (row, ri) {
        row.forEach(function (v, ci) {
            var t = v / max;
            var high = getActiveDashboard().heatmapHigh;
            var low = getActiveDashboard().heatmapLow;
            group.add(app.rect({
                x: ci * cw,
                y: ri * ch,
                width: cw - 1,
                height: ch - 1,
                fill: mixHexColors(low, high, t),
                listening: false,
            }));
        });
    });
    attachGridHover(app, group, props, width, height, rows, cols, function (ri, ci) { return String(matrix[ri][ci]); });
    setState(group, { width: width, height: height, matrix: matrix });
    return group;
});
registerDashboard('hexbinChart', function (props, app) {
    var e_1, _a;
    var _b;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var points = (_b = props.points) !== null && _b !== void 0 ? _b : Array.from({ length: 40 }, function () { return ({ x: Math.random() * 280, y: Math.random() * 130 }); });
    var group = createWidgetGroup(app, 'hexbinChart', props);
    group.add(app.rect({ width: width, height: height, fill: getActiveDashboard().chartBg, listening: true }));
    var bins = hexbinCenters(points.map(function (p) { return [p.x, p.y]; }), width, height, 12);
    var max = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(__spreadArray([], __read(bins.values()), false).map(function (b) { return b.count; })), false), [1], false));
    try {
        for (var _d = __values(bins.values()), _e = _d.next(); !_e.done; _e = _d.next()) {
            var b = _e.value;
            group.add(app.circle({ x: b.x - 10, y: b.y - 10, radius: 10, fill: getActiveDashboard().primary, opacity: b.count / max, listening: false }));
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
        }
        finally { if (e_1) throw e_1.error; }
    }
    attachNearestHover(app, group, props, { x: 0, y: 0, width: width, height: height }, __spreadArray([], __read(bins.values()), false).map(function (b) { return ({ x: b.x, y: b.y, label: "count: ".concat(b.count) }); }));
    setState(group, { width: width, height: height, points: points });
    return group;
});
registerDashboard('contourChart', function (props, app) {
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var group = createWidgetGroup(app, 'contourChart', props);
    group.add(app.rect({ width: width, height: height, fill: getActiveDashboard().chartBg, listening: true }));
    for (var i = 1; i <= 5; i++) {
        var inset = i * 12;
        group.add(app.rect({ x: inset, y: inset, width: width - inset * 2, height: height - inset * 2, fill: null, stroke: getActiveDashboard().chartLine, strokeWidth: 1, listening: false }));
    }
    attachGridHover(app, group, props, width, height, 5, 5, function (_r, _c) { return 'contour'; });
    setState(group, { width: width, height: height });
    return group;
});
registerDashboard('qqPlot', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var data = (_a = props.data) !== null && _a !== void 0 ? _a : [2, 3, 3, 4, 5, 5, 6, 7, 8, 9];
    var sorted = __spreadArray([], __read(data), false).sort(function (a, b) { return a - b; });
    var theoretical = normalQuantiles(sorted.length);
    var group = createWidgetGroup(app, 'qqPlot', props);
    var bounds = dataBounds(__spreadArray(__spreadArray([], __read(sorted), false), __read(theoretical), false));
    var layout = plotChrome(app, group, width, height, bounds);
    sorted.forEach(function (_v, i) {
        var x = layout.plotX + (layout.plotWidth * i) / Math.max(sorted.length - 1, 1);
        var tx = theoretical[i];
        var range = bounds.max - bounds.min || 1;
        var py = layout.plotY + layout.plotHeight - ((tx - bounds.min) / range) * layout.plotHeight;
        group.add(app.circle({ x: x - 3, y: py - 3, radius: 3, fill: getActiveDashboard().chartDot, listening: false }));
    });
    attachIndexXHover(app, group, props, layout, sorted.length, function (i) { return "obs ".concat(sorted[i], " / q ").concat(theoretical[i].toFixed(2)); });
    setState(group, { width: width, height: height, data: data });
    return group;
});
registerDashboard('beeswarmChart', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var data = (_a = props.data) !== null && _a !== void 0 ? _a : [2, 3, 3, 4, 5, 5, 6, 7, 8, 9];
    var group = createWidgetGroup(app, 'beeswarmChart', props);
    var layout = plotChrome(app, group, width, height, dataBounds(data));
    var cx = layout.plotX + layout.plotWidth / 2;
    var db = dataBounds(data);
    data.forEach(function (v, i) {
        var y = layout.plotY + layout.plotHeight - ((v - db.min) / (db.max - db.min || 1)) * layout.plotHeight;
        var x = cx + ((i % 7) - 3) * 8;
        group.add(app.circle({ x: x - 3, y: y - 3, radius: 4, fill: getActiveDashboard().chartDot, listening: false }));
    });
    attachNearestHover(app, group, props, { x: layout.plotX, y: layout.plotY, width: layout.plotWidth, height: layout.plotHeight }, data.map(function (v, i) {
        var y = layout.plotY + layout.plotHeight - ((v - db.min) / (db.max - db.min || 1)) * layout.plotHeight;
        var x = cx + ((i % 7) - 3) * 8;
        return { x: x, y: y, label: String(v) };
    }));
    setState(group, { width: width, height: height, data: data });
    return group;
});
registerDashboard('ridgelinePlot', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 200);
    var series = (_a = props.series) !== null && _a !== void 0 ? _a : [[2, 3, 4, 5], [3, 5, 7, 9], [1, 2, 3, 8]];
    var group = createWidgetGroup(app, 'ridgelinePlot', props);
    group.add(app.rect({ width: width, height: height, fill: getActiveDashboard().chartBg, listening: true }));
    var band = height / series.length;
    series.forEach(function (data, si) {
        var bounds = dataBounds(data);
        var y0 = si * band + 10;
        var pts = [];
        data.forEach(function (v, i) {
            pts.push(30 + (width - 60) * (i / Math.max(data.length - 1, 1)), y0 + band - 10 - ((v - bounds.min) / (bounds.max - bounds.min || 1)) * (band - 20));
        });
        group.add(app.polyline({ points: pts, fill: null, stroke: getActiveDashboard().series[si % getActiveDashboard().series.length], strokeWidth: 2, listening: false }));
    });
    attachBandYHover(app, group, props, width, height, series.length, function (i) { return "series ".concat(i + 1); });
    setState(group, { width: width, height: height, series: series });
    return group;
});
registerDashboard('parallelCoordinatesPlot', function (props, app) {
    var _a, _b, _d;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var dimensions = (_a = props.dimensions) !== null && _a !== void 0 ? _a : [[1, 5, 3], [2, 4, 6], [3, 2, 8]];
    var group = createWidgetGroup(app, 'parallelCoordinatesPlot', props);
    group.add(app.rect({ width: width, height: height, fill: getActiveDashboard().chartBg, listening: true }));
    var cols = (_d = (_b = dimensions[0]) === null || _b === void 0 ? void 0 : _b.length) !== null && _d !== void 0 ? _d : 3;
    var step = (width - 40) / Math.max(cols - 1, 1);
    for (var c = 0; c < cols; c++) {
        var x = 20 + c * step;
        group.add(app.line({ x: x, y: 20, x2: 0, y2: height - 40, stroke: getActiveDashboard().chartGrid, strokeWidth: 1, listening: false }));
    }
    dimensions.forEach(function (row, ri) {
        var pts = [];
        var max = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(row), false), [1], false));
        row.forEach(function (v, ci) {
            pts.push(20 + ci * step, height - 20 - (v / max) * (height - 40));
        });
        group.add(app.polyline({ points: pts, fill: null, stroke: getActiveDashboard().series[ri % getActiveDashboard().series.length], strokeWidth: 1.5, listening: false }));
    });
    attachIndexXHover(app, group, props, { plotX: 20, plotY: 20, plotWidth: width - 40, plotHeight: height - 40 }, cols, function (i) { return "dim ".concat(i + 1); });
    setState(group, { width: width, height: height, dimensions: dimensions });
    return group;
});
registerDashboard('mosaicChart', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var cells = (_a = props.cells) !== null && _a !== void 0 ? _a : [
        { w: 0.5, h: 0.5, value: 1 },
        { w: 0.5, h: 0.5, value: 2 },
        { w: 0.3, h: 0.5, value: 3 },
        { w: 0.7, h: 0.5, value: 4 },
    ];
    var group = createWidgetGroup(app, 'mosaicChart', props);
    var x = 0;
    var y = 0;
    var regions = [];
    cells.forEach(function (c, i) {
        var cw = c.w * width;
        var ch = c.h * height;
        regions.push({ x: x, y: y, width: cw - 1, height: ch - 1, label: String(c.value) });
        group.add(app.rect({ x: x, y: y, width: cw - 1, height: ch - 1, fill: getActiveDashboard().series[i % getActiveDashboard().series.length], listening: false }));
        x += cw;
        if (x >= width - 1) {
            x = 0;
            y += ch;
        }
    });
    attachRegionsHover(app, group, props, width, height, regions);
    setState(group, { width: width, height: height, cells: cells });
    return group;
});
registerDashboard('marimekkoChart', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var segments = (_a = props.segments) !== null && _a !== void 0 ? _a : [
        { widthFrac: 0.4, heightFrac: 0.6 },
        { widthFrac: 0.6, heightFrac: 0.4 },
        { widthFrac: 0.5, heightFrac: 0.5 },
    ];
    var group = createWidgetGroup(app, 'marimekkoChart', props);
    var x = 0;
    var regions = [];
    segments.forEach(function (s, i) {
        var w = s.widthFrac * width;
        var h = s.heightFrac * height;
        regions.push({ x: x, y: height - h, width: w - 1, height: h, label: "".concat(Math.round(s.widthFrac * 100), "%") });
        group.add(app.rect({ x: x, y: height - h, width: w - 1, height: h, fill: getActiveDashboard().series[i % getActiveDashboard().series.length], listening: false }));
        x += w;
    });
    attachRegionsHover(app, group, props, width, height, regions);
    setState(group, { width: width, height: height, segments: segments });
    return group;
});
registerDashboard('mekkoChart', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var segments = (_a = props.segments) !== null && _a !== void 0 ? _a : [
        { widthFrac: 0.4, heightFrac: 0.6 },
        { widthFrac: 0.6, heightFrac: 0.4 },
        { widthFrac: 0.5, heightFrac: 0.5 },
    ];
    var group = createWidgetGroup(app, 'mekkoChart', props);
    var x = 0;
    var regions = [];
    segments.forEach(function (s, i) {
        var w = s.widthFrac * width;
        var h = s.heightFrac * height;
        regions.push({ x: x, y: height - h, width: w - 1, height: h, label: "".concat(Math.round(s.widthFrac * 100), "%") });
        group.add(app.rect({ x: x, y: height - h, width: w - 1, height: h, fill: getActiveDashboard().series[i % getActiveDashboard().series.length], listening: false }));
        x += w;
    });
    attachRegionsHover(app, group, props, width, height, regions);
    setState(group, { width: width, height: height, segments: segments });
    return group;
});
registerDashboard('waffleChart', function (props, app) {
    var size = num(props, 'size', 160);
    var total = num(props, 'total', 100);
    var value = num(props, 'value', 42);
    var group = createWidgetGroup(app, 'waffleChart', props);
    var grid = 10;
    var cell = size / grid;
    var filled = Math.round((value / total) * grid * grid);
    for (var i = 0; i < grid * grid; i++) {
        var col = i % grid;
        var row = Math.floor(i / grid);
        group.add(app.rect({
            x: col * cell,
            y: row * cell,
            width: cell - 2,
            height: cell - 2,
            fill: i < filled ? getActiveDashboard().primary : getActiveDashboard().inactive,
            cornerRadius: 2,
            listening: false,
        }));
    }
    attachGridHover(app, group, props, size, size, grid, grid, function (_r, c) {
        var idx = _r * grid + c;
        return idx < filled ? 'filled' : 'empty';
    });
    setState(group, { size: size, total: total, value: value });
    return group;
});
registerDashboard('calendarHeatmap', function (props, app) {
    var group = createWidgetGroup(app, 'calendarHeatmap', props);
    installChartRebuild(group, app, buildCalendarHeatmap);
    return group;
});
function buildCalendarHeatmap(group, app, props) {
    var _a;
    var width = num(props, 'width', 280);
    var height = num(props, 'height', 120);
    var values = (_a = props.values) !== null && _a !== void 0 ? _a : Array.from({ length: 35 }, function (_, i) { return (i % 7) + 1; });
    var max = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(values), false), [1], false));
    var cols = 7;
    var rows = Math.ceil(values.length / cols);
    var gap = 2;
    var cellW = (width - gap * (cols - 1)) / cols;
    var cellH = (height - gap * (rows - 1)) / rows;
    var cell = Math.max(4, Math.floor(Math.min(cellW, cellH)));
    var gridW = cols * cell + gap * (cols - 1);
    var gridH = rows * cell + gap * (rows - 1);
    var offsetX = Math.max(0, (width - gridW) / 2);
    var offsetY = Math.max(0, (height - gridH) / 2);
    values.forEach(function (v, i) {
        var col = i % cols;
        var row = Math.floor(i / cols);
        var t = v / max;
        group.add(app.rect({
            x: offsetX + col * (cell + gap),
            y: offsetY + row * (cell + gap),
            width: cell,
            height: cell,
            fill: primaryAlpha(t),
            cornerRadius: Math.min(2, cell * 0.15),
            listening: false,
        }));
    });
    attachGridHover(app, group, props, width, height, rows, cols, function (row, col) {
        var v = values[row * cols + col];
        return v != null ? String(v) : '';
    });
    setState(group, { width: width, height: height, values: values });
}
registerDashboard('stemLeafPlot', function (props, app) {
    var e_2, _a;
    var _b;
    var width = num(props, 'width', 200);
    var height = num(props, 'height', 180);
    var data = (_b = props.data) !== null && _b !== void 0 ? _b : [12, 23, 23, 34, 45, 56, 67, 78, 89];
    var group = createWidgetGroup(app, 'stemLeafPlot', props);
    var stems = new Map();
    data.forEach(function (v) {
        var stem = Math.floor(v / 10);
        var leaf = v % 10;
        if (!stems.has(stem))
            stems.set(stem, []);
        stems.get(stem).push(leaf);
    });
    var y = 8;
    try {
        for (var _d = __values(__spreadArray([], __read(stems.entries()), false).sort(function (a, b) { return a[0] - b[0]; })), _e = _d.next(); !_e.done; _e = _d.next()) {
            var _f = __read(_e.value, 2), stem = _f[0], leaves = _f[1];
            group.add(app.text({ text: "".concat(stem, " | ").concat(leaves.join(' ')), x: 8, y: y, fontSize: 12, fill: getActiveDashboard().text, listening: false }));
            y += 16;
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
        }
        finally { if (e_2) throw e_2.error; }
    }
    var rowCount = stems.size;
    attachBandYHover(app, group, props, width, height, rowCount, function (i) {
        var _a;
        var entries = __spreadArray([], __read(stems.entries()), false).sort(function (a, b) { return a[0] - b[0]; });
        var _b = __read((_a = entries[i]) !== null && _a !== void 0 ? _a : [0, []], 2), stem = _b[0], leaves = _b[1];
        return "".concat(stem, " | ").concat(leaves.join(' '));
    });
    setState(group, { width: width, height: height, data: data });
    return group;
});
registerDashboard('scatterChart', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var points = (_a = props.points) !== null && _a !== void 0 ? _a : [
        { x: 10, y: 20 },
        { x: 30, y: 45 },
        { x: 50, y: 35 },
        { x: 70, y: 80 },
        { x: 90, y: 60 },
    ];
    var group = createWidgetGroup(app, 'scatterChart', props);
    var xs = points.map(function (p) { return p.x; });
    var ys = points.map(function (p) { return p.y; });
    var layout = plotChrome(app, group, width, height, { min: Math.min.apply(Math, __spreadArray(__spreadArray([], __read(ys), false), [0], false)), max: Math.max.apply(Math, __spreadArray(__spreadArray([], __read(ys), false), [1], false)) });
    var xScale = linearScale([Math.min.apply(Math, __spreadArray([], __read(xs), false)), Math.max.apply(Math, __spreadArray([], __read(xs), false))], [layout.plotX, layout.plotX + layout.plotWidth]);
    var yScale = linearScale([Math.min.apply(Math, __spreadArray([], __read(ys), false)), Math.max.apply(Math, __spreadArray([], __read(ys), false))], [layout.plotY + layout.plotHeight, layout.plotY]);
    points.forEach(function (p) {
        group.add(app.circle({ x: xScale(p.x) - 4, y: yScale(p.y) - 4, radius: 4, fill: getActiveDashboard().chartDot, listening: false }));
    });
    attachNearestHover(app, group, props, { x: layout.plotX, y: layout.plotY, width: layout.plotWidth, height: layout.plotHeight }, points.map(function (p) { return ({
        x: xScale(p.x),
        y: yScale(p.y),
        label: "(".concat(p.x, ", ").concat(p.y, ")"),
    }); }));
    setState(group, { width: width, height: height, points: points });
    return group;
});
registerDashboard('bubbleChart', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 150);
    var points = (_a = props.points) !== null && _a !== void 0 ? _a : [
        { x: 20, y: 30, size: 10 },
        { x: 50, y: 60, size: 25 },
        { x: 80, y: 40, size: 15 },
    ];
    var group = createWidgetGroup(app, 'bubbleChart', props);
    var xs = points.map(function (p) { return p.x; });
    var ys = points.map(function (p) { return p.y; });
    var layout = plotChrome(app, group, width, height, { min: Math.min.apply(Math, __spreadArray(__spreadArray([], __read(ys), false), [0], false)), max: Math.max.apply(Math, __spreadArray(__spreadArray([], __read(ys), false), [1], false)) });
    var xScale = linearScale([Math.min.apply(Math, __spreadArray([], __read(xs), false)), Math.max.apply(Math, __spreadArray([], __read(xs), false))], [layout.plotX, layout.plotX + layout.plotWidth]);
    var yScale = linearScale([Math.min.apply(Math, __spreadArray([], __read(ys), false)), Math.max.apply(Math, __spreadArray([], __read(ys), false))], [layout.plotY + layout.plotHeight, layout.plotY]);
    points.forEach(function (p) {
        var _a;
        var r = ((_a = p.size) !== null && _a !== void 0 ? _a : 8) / 2;
        group.add(app.circle({ x: xScale(p.x) - r, y: yScale(p.y) - r, radius: r, fill: getActiveDashboard().chartArea, stroke: getActiveDashboard().chartLine, strokeWidth: 1, listening: false }));
    });
    attachNearestHover(app, group, props, { x: layout.plotX, y: layout.plotY, width: layout.plotWidth, height: layout.plotHeight }, points.map(function (p) {
        var _a;
        return ({
            x: xScale(p.x),
            y: yScale(p.y),
            label: "(".concat(p.x, ", ").concat(p.y, ") r=").concat((_a = p.size) !== null && _a !== void 0 ? _a : 8),
        });
    }));
    setState(group, { width: width, height: height, points: points });
    return group;
});
